import { useEffect, useState, type CSSProperties } from "react";
import { KeyRound, LockKeyhole, Newspaper, ShieldCheck } from "lucide-react";
import { loadAuthConfig } from "@/api";
import { HumanGate } from "@/components/auth/HumanGate";
import { useBrand } from "@/lib/brandConfig";
import { useT } from "@/lib/i18nConfig";
import { cn } from "@/lib/utils";

const monoTnum: CSSProperties = {
  fontVariantNumeric: "tabular-nums",
  fontFeatureSettings: '"tnum" 1, "zero" 1',
};

function MiniMeta({ children }: { children: string }) {
  return (
    <span className="text-[10.5px] font-black uppercase tracking-[0.16em] text-[var(--portal-muted)]">
      {children}
    </span>
  );
}

function formatDuration(seconds: number, t: (key: string, vars?: Record<string, string | number>) => string): string {
  if (seconds % 86_400 === 0) return t("login.duration.days", { n: seconds / 86_400 });
  if (seconds % 3_600 === 0) return t("login.duration.hours", { n: seconds / 3_600 });
  if (seconds % 60 === 0) return t("login.duration.minutes", { n: seconds / 60 });
  return t("login.duration.seconds", { n: seconds });
}

const LOGIN_ERROR_TRANSLATIONS: Record<string, string> = {
  "登录暂时不可用，请稍后重试。": "login.error.unavailable",
  "服务暂时不可用，请稍后重试。": "login.error.serviceUnavailable",
  "请求频率过高，请稍后再试。": "login.error.rateLimited",
  "登录失败次数过多，请稍后再试。": "login.error.attemptsExceeded",
  "登录请求格式错误，请重试。": "login.error.invalidRequest",
  "请输入账号和密码。": "login.error.credentialsRequired",
  "登录未配置，请先设置 AXIOM_WEB_PASSWORD。": "login.error.notConfigured",
  "人机验证未完成，请等待校验后重试。": "login.error.verificationRequired",
  "人机验证失败，请刷新页面后重试。": "login.error.verificationFailed",
  "账号已被临时锁定，请 10 分钟后重试。": "login.error.locked",
  "账号或密码不正确。": "login.error.invalidCredentials",
  "会话创建失败，请稍后重试。": "login.error.sessionFailed",
};

export function LoginPage() {
  const t = useT();
  const brand = useBrand();
  const [humanGateEnabled, setHumanGateEnabled] = useState(false);
  const [humanGateVerified, setHumanGateVerified] = useState(false);
  const [sessionTtlSeconds, setSessionTtlSeconds] = useState(8 * 3_600);
  const [configError, setConfigError] = useState("");
  const sessionTtlLabel = formatDuration(sessionTtlSeconds, t);
  const canSubmit = !humanGateEnabled || humanGateVerified;

  const urlError = new URLSearchParams(window.location.search).get("error") ?? "";
  const errorMessage = configError || (LOGIN_ERROR_TRANSLATIONS[urlError] ? t(LOGIN_ERROR_TRANSLATIONS[urlError]) : urlError);

  useEffect(() => {
    loadAuthConfig()
      .then((payload) => {
        setHumanGateEnabled(Boolean(payload.humanGateEnabled));
        setSessionTtlSeconds(payload.sessionTtlSeconds ?? 8 * 3_600);
      })
      .catch((exc: unknown) => {
        setConfigError(exc instanceof Error ? exc.message : t("login.error.fallback"));
      });
  }, [t]);

  return (
    <div
      className={cn(
        "relative w-full overflow-hidden border-y-2 px-4 py-4 text-[14px] font-semibold transition-colors duration-[180ms] sm:px-6 sm:py-5",
        "text-[var(--portal-text)]",
      )}
      style={{
        "--portal-bg": "#F6F0E4",
        "--portal-card": "#FFFDF7",
        "--portal-border": "#22201B",
        "--portal-border-soft": "#CBC1AE",
        "--portal-text": "#17130D",
        "--portal-muted": "#665D50",
        "--portal-accent": "#8B1E16",
        "--portal-control": "#FBF7ED",
        "--portal-serif": 'Georgia, "Times New Roman", "Noto Serif SC", "Source Han Serif SC", "Songti SC", SimSun, serif',
        "--portal-sans": '"Noto Sans SC", "Microsoft YaHei UI", "PingFang SC", "SF Pro Text", system-ui, sans-serif',
        background:
          "linear-gradient(90deg, rgba(34,32,27,0.045) 1px, transparent 1px) 0 0 / 24px 24px, var(--portal-bg)",
        borderColor: "var(--portal-border)",
        fontFamily: "var(--portal-sans)",
      } as CSSProperties}
    >
      <div className="mx-auto w-full max-w-[1120px]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--portal-border)] pb-2 text-[11px] font-black uppercase tracking-[0.16em] text-[var(--portal-muted)]">
          <span>{t("login.access")}</span>
          <span className="font-mono" style={monoTnum}>
            ROOT / 6.13
          </span>
          <span>{t("login.status.waiting")}</span>
        </div>

        <header className="border-b-[3px] border-[var(--portal-border)] py-3 text-center sm:py-4">
          <div className="mx-auto flex max-w-[900px] items-center justify-center gap-3">
            <span className="hidden h-px flex-1 bg-[var(--portal-border)] sm:block" aria-hidden />
            <Newspaper className="size-5 text-[var(--portal-accent)]" strokeWidth={2.2} aria-hidden />
            <MiniMeta>Private Edition</MiniMeta>
            <span className="hidden h-px flex-1 bg-[var(--portal-border)] sm:block" aria-hidden />
          </div>
          <h1
            className="mt-2 text-balance text-[clamp(2.45rem,7vw,5.2rem)] font-black leading-[0.9] tracking-normal text-[var(--portal-text)]"
            style={{ fontFamily: "var(--portal-serif)" }}
          >
            {brand.brandName}
          </h1>
          <p className="mx-auto mt-2 max-w-[720px] text-balance text-[14px] font-bold leading-6 text-[var(--portal-muted)] sm:text-[15px]">
            {t("login.description")}
          </p>
        </header>

        <main className="grid gap-5 pt-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(360px,0.95fr)] lg:gap-8">
          <section className="order-2 grid content-start gap-4 lg:order-1">
            <div className="grid border-y border-[var(--portal-border)] py-2.5 sm:grid-cols-[1fr_auto_1fr] sm:items-center sm:gap-4">
              <p
                className="text-[clamp(1.55rem,3.4vw,3.05rem)] font-black leading-[0.95] text-[var(--portal-text)]"
                style={{ fontFamily: "var(--portal-serif)" }}
              >
                Authorized access, printed in ink.
              </p>
              <span className="my-3 h-px bg-[var(--portal-border-soft)] sm:my-0 sm:h-full sm:w-px" aria-hidden />
              <p className="text-[14px] font-semibold leading-7 text-[var(--portal-muted)] sm:text-[15px]">
                {t("login.session", { x: sessionTtlLabel })}
              </p>
            </div>

            <div className="grid gap-0 border-y border-[var(--portal-border)] sm:grid-cols-3 sm:divide-x sm:divide-[var(--portal-border)]">
              <div className="px-0 py-2.5 sm:px-4">
                <MiniMeta>{t("login.status")}</MiniMeta>
                <p className="mt-1 text-[15px] font-black text-[var(--portal-text)]">{t("login.status.waiting")}</p>
              </div>
              <div className="border-t border-[var(--portal-border)] px-0 py-2.5 sm:border-t-0 sm:px-4">
                <MiniMeta>{t("login.session.label")}</MiniMeta>
                <p className="mt-1 font-mono text-[15px] font-black text-[var(--portal-text)]" style={monoTnum}>
                  {sessionTtlLabel}
                </p>
              </div>
              <div className="border-t border-[var(--portal-border)] px-0 py-2.5 sm:border-t-0 sm:px-4">
                <MiniMeta>{t("login.privacy")}</MiniMeta>
                <p className="mt-1 text-[15px] font-black text-[var(--portal-text)]">{t("login.privacy.value")}</p>
              </div>
            </div>
          </section>

          <aside className="order-1 lg:order-2">
            <div
              className={cn(
                "border-2 bg-[var(--portal-card)] p-4 shadow-[12px_12px_0_0_rgba(34,32,27,0.14)] sm:p-4",
                "border-[var(--portal-border)] transition-colors duration-[180ms]",
              )}
            >
              <header className="flex items-start justify-between gap-5 border-b-2 border-[var(--portal-border)] pb-3">
                <div className="min-w-0 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <MiniMeta>{t("login.access")}</MiniMeta>
                    <span className="h-3 w-px bg-[var(--portal-border-soft)]" aria-hidden />
                    <span className="font-mono text-[10.5px] font-black uppercase tracking-[0.16em] text-[var(--portal-muted)]" style={monoTnum}>
                      Desk 01
                    </span>
                  </div>
                  <div className="space-y-1">
                    <h2
                      className="text-[28px] font-black leading-none tracking-normal sm:text-[32px]"
                      style={{ fontFamily: "var(--portal-serif)" }}
                    >
                      Sign In
                    </h2>
                    <p className="max-w-[520px] text-[14px] font-semibold leading-6 text-[var(--portal-muted)]">
                      {brand.brandName}
                    </p>
                  </div>
                </div>
                <span
                  className={cn(
                    "flex size-11 shrink-0 items-center justify-center border-2",
                    "border-[var(--portal-border)] bg-[var(--portal-control)] text-[var(--portal-text)]",
                  )}
                  aria-hidden
                >
                  <LockKeyhole className="size-5" strokeWidth={2.1} />
                </span>
              </header>

              <form method="post" action="/api/login" className="pt-4">
                <div className="grid gap-3">
                  <label className="space-y-2">
                    <span className="flex items-center gap-2">
                      <ShieldCheck className="size-4 text-[var(--portal-accent)]" strokeWidth={2.1} />
                      <MiniMeta>{t("login.username")}</MiniMeta>
                    </span>
                    <input
                      id="username"
                      name="username"
                      autoComplete="username"
                      required
                      className={cn(
                        "h-11 w-full rounded-[3px] border-2 px-3.5 text-[15px] font-bold outline-none transition-colors duration-[180ms]",
                        "border-[var(--portal-border)] bg-[var(--portal-control)] text-[var(--portal-text)]",
                        "hover:bg-white focus:border-[var(--portal-accent)] focus:bg-white focus:ring-0",
                      )}
                    />
                  </label>
                  <label className="space-y-2">
                    <span className="flex items-center gap-2">
                      <KeyRound className="size-4 text-[var(--portal-accent)]" strokeWidth={2.1} />
                      <MiniMeta>{t("login.password")}</MiniMeta>
                    </span>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      className={cn(
                        "h-11 w-full rounded-[3px] border-2 px-3.5 text-[15px] font-bold outline-none transition-colors duration-[180ms]",
                        "border-[var(--portal-border)] bg-[var(--portal-control)] text-[var(--portal-text)]",
                        "hover:bg-white focus:border-[var(--portal-accent)] focus:bg-white focus:ring-0",
                      )}
                    />
                  </label>
                </div>

                {humanGateEnabled ? <HumanGate onVerifiedChange={setHumanGateVerified} /> : null}

                {errorMessage ? (
                  <div className="mt-3 rounded-[3px] border-2 border-[var(--danger)]/60 bg-[var(--danger)]/5 px-3.5 py-2.5 text-[13px] font-bold leading-5 text-[var(--danger)]">
                    {errorMessage}
                  </div>
                ) : null}

                <div className="mt-4 grid gap-2.5">
                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className={cn(
                      "h-11 rounded-[3px] border-2 px-4 text-[13px] font-black uppercase tracking-[0.14em] transition-colors duration-[180ms]",
                      "border-[var(--portal-border)] bg-[var(--portal-text)] text-[var(--portal-card)]",
                      "hover:bg-[var(--portal-accent)] focus:outline-none focus:ring-0",
                      !canSubmit && "cursor-not-allowed opacity-50 hover:bg-[var(--portal-text)]",
                    )}
                  >
                    {t("login.enter")}
                  </button>
                  <p className="text-center text-[12.5px] font-bold leading-5 text-[var(--portal-muted)]">
                    {t("login.session", { x: sessionTtlLabel })}
                  </p>
                </div>
              </form>
            </div>
          </aside>
        </main>
      </div>
    </div>
  );
}
